import { HttpLink } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { ErrorBoundary } from '@fergdigitalcommerce/fergy-core-react-web/esmDist/components/error-boundary';
import { createBrowserApolloClient } from '@fergdigitalcommerce/fergy-gql/esmDist/browser';
import { browserFetchWrapper } from '@fergdigitalcommerce/fergy-gql/esmDist/browser/apollo-browser-client-creator';
import { loadableReady } from '@loadable/component';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { defaultFallbackInView } from 'react-intersection-observer';
import { BrowserRouter } from 'react-router-dom';
import { DEFAULT_PHONE } from '../constants/contact-info';
import { createCache } from './cache';
import { App } from './components/app';
import { ErrorContent } from './components/error-components/error-common/error-common.component';
import { akamaiErrorLink } from './helpers/apollo/akamai-error-link';
import { dataDogRumLink } from './helpers/apollo/datadog-rum-link';

// We polyfill IntersectionObserver on older browsers, but users
// can still turn it off on some browsers like Safari.  Provide a fallback
// so we don't get script errors from react-intersection-observer in those cases.
defaultFallbackInView(true);

// Create the apollo client.
const apolloClient = createBrowserApolloClient({
	cache: createCache(),
	apolloLinks: [akamaiErrorLink(), dataDogRumLink],
	terminatingLink: new HttpLink({
		fetch: browserFetchWrapper,
		credentials: 'same-origin'
	})
});

// Disable the client network fetches until after the app has hydrated from the server.
// This will prevent re-fetching of already cached data during the SSR.
apolloClient.prioritizeCacheValues = true;

function AppWithCallbackAfterRender() {
	useEffect(() => {
		// Re-enable the apollo client now that it has fully hydrated.
		apolloClient.prioritizeCacheValues = false;
	}, []);

	return <App />;
}

const mount = (RootComponent: FunctionComponent) => {
	hydrateRoot(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		document.getElementById('root') as Element,
		<ErrorBoundary fallback={<ErrorContent phoneNumber={DEFAULT_PHONE} />}>
			<ApolloProvider client={apolloClient}>
				<BrowserRouter>
					<HelmetProvider>
						<RootComponent />
					</HelmetProvider>
				</BrowserRouter>
			</ApolloProvider>
		</ErrorBoundary>,
		{
			onRecoverableError(error, info) {
				logError(error, { componentStack: info.componentStack });
			}
		}
	);
};

loadableReady(() => {
	mount(AppWithCallbackAfterRender);
}).catch(logError);
